import { useState, useEffect } from "react";
import { Attachment as AttachmentProto } from "protogen/common_pb";
import { Box, Card, CardActionArea, CardHeader } from "@mui/material";
import FileDownload from "@mui/icons-material/FileDownload";
import ContactCardEntry from "../common/ContactCardEntry";
import { withImageProps, withVideoProps } from "../common/SignedImageCache";
import { humanFileSize } from "../../common/utils";
import { useConvertAmrAudio } from "services/files";

const errorBorder = { border: "2px solid red", boxShadow: "0 0 3px red" };

const DefaultAttachment = ({
  attachment,
  loading,
  error,
}: {
  attachment: AttachmentProto;
  loading?: boolean;
  error?: string;
}) => {
  return (
    <Card
      onClick={() => {
        window.open(attachment.url, "Download");
      }}
      sx={{
        maxWidth: 345,
        ...(loading ? { filter: "blur(3px)" } : {}),
        ...(error ? { border: errorBorder } : {}),
      }}
    >
      <CardActionArea>
        <CardHeader
          avatar={<FileDownload />}
          title={attachment.filename}
          subheader={humanFileSize(attachment.fileSize)}
        />
      </CardActionArea>
    </Card>
  );
};

const AMRAudioPlayer = ({ attachment }: { attachment: AttachmentProto }) => {
  const { request } = useConvertAmrAudio();
  const [convertedUrl, setConvertedUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    const convertAMRToWav = async () => {
      try {
        setLoading(true);
        setError(false);

        const response = await request({ url: attachment.url });
        if (response?.data) {
          const binaryString = window.atob(response.data);
          const len = binaryString.length;
          const bytes = new Uint8Array(len);
          for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
          }
          const blob = new Blob([bytes], { type: "audio/mpeg" });
          const objectUrl = URL.createObjectURL(blob);
          setConvertedUrl(objectUrl);

          return () => URL.revokeObjectURL(objectUrl);
        }
      } catch (err) {
        console.error("AMR conversion failed", err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    convertAMRToWav();
  }, []);

  return (
    <Box>
      <audio
        controls
        style={{
          width: "100%",
          minWidth: "350px",
          borderRadius: "12px",
          cursor: "pointer",
          marginTop: "6px",
          ...(loading ? { filter: "blur(4px)" } : {}),
          ...(error ? { border: "2px solid red" } : {}),
        }}
      >
        {convertedUrl && <source src={convertedUrl} type="audio/mpeg" />}
      </audio>
      {!loading && !convertedUrl && (
        <DefaultAttachment attachment={attachment} loading={loading} />
      )}
    </Box>
  );
};

export default ({
  attachment,
  openAttachment,
  loading,
  error,
}: {
  attachment: AttachmentProto;
  openAttachment: (a: AttachmentProto) => void;
  loading?: boolean;
  error?: string | undefined;
}) => {
  const errorBorder = { border: "2px solid red", boxShadow: "0 0 3px red" };
  if (attachment.contentType.startsWith("image")) {
    return (
      <img
        onClick={() => openAttachment(attachment)}
        {...withImageProps({ url: attachment.url })}
        alt={attachment.filename}
        style={{
          minHeight: "20px",
          minWidth: "20px",
          maxWidth: "100%",
          maxHeight: "30vh",
          borderRadius: "12px",
          cursor: "pointer",
          marginTop: "6px",
          // Handle long alt text.
          textOverflow: "ellipsis",
          overflowX: "hidden",
          ...{ textWrap: "nowrap" },
          ...(loading ? { filter: "blur(4px)" } : {}),
          ...(error ? errorBorder : {}),
        }}
      />
    );
  } else if (
    attachment.contentType.startsWith("video") &&
    attachment.contentType.toLowerCase() !== "video/3gpp"
  ) {
    const videoProps = withVideoProps({ url: attachment.url });
    return (
      <video
        width="320"
        height="240"
        onError={videoProps.onError}
        controls
        style={{
          minHeight: "20px",
          minWidth: "20px",
          maxWidth: "100%",
          maxHeight: "30vh",
          borderRadius: "12px",
          cursor: "pointer",
          marginTop: "6px",
          // Handle long alt text.
          textOverflow: "ellipsis",
          overflowX: "hidden",
          ...{ textWrap: "nowrap" },
          ...(loading ? { filter: "blur(4px)" } : {}),
          ...(error ? errorBorder : {}),
        }}
      >
        <source
          src={videoProps.src}
          type={"video/mp4"}
          // type={attachment.contentType}
        />
        Your browser does not support the video tag.
      </video>
    );
  } else if (
    [
      "audio/mpeg",
      "audio/mp3",
      "audio/aac",
      "audio/ogg",
      "audio/wav",
      "audio/flac",
    ].indexOf(attachment.contentType.toLowerCase()) >= 0
  ) {
    return (
      <audio
        controls
        style={{
          width: "100%",
          minWidth: "350px",
          borderRadius: "12px",
          cursor: "pointer",
          marginTop: "6px",
          ...(loading ? { filter: "blur(4px)" } : {}),
          ...(error ? errorBorder : {}),
        }}
      >
        <source src={attachment.url} type={attachment.contentType} />
        Your browser does not support the audio tag.
      </audio>
    );
  } else if (["audio/amr"].indexOf(attachment.contentType.toLowerCase()) >= 0) {
    return <AMRAudioPlayer attachment={attachment} />;
  } else if (
    ["text/x-vcard", "text/vcard"].indexOf(
      attachment.contentType.toLowerCase(),
    ) >= 0
  ) {
    return (
      <div
        style={{
          marginTop: "10px",
          ...(loading ? { filter: "blur(4px)" } : {}),
          ...(error ? errorBorder : {}),
        }}
      >
        <ContactCardEntry
          vcfUrl={attachment.url}
          // For now, downloading a contact card is less desirable than copying the text.
          // onClick={() => { window.open(attachment.url, "Download"); }}
        />
      </div>
    );
  } else {
    return (
      <DefaultAttachment
        attachment={attachment}
        loading={loading}
        error={error}
      />
    );
  }
};
